import { AlertTriangle, LifeBuoy, PhoneCall } from "lucide-react";
import { useEffect, useState } from "react";

import { CallView } from "@/support/CallView";
import { useSupport } from "@/support/SupportContext";

function Countdown({ expiresAt }: { expiresAt: string }) {
  const [rimasti, setRimasti] = useState(() =>
    Math.max(0, Math.round((new Date(expiresAt).getTime() - Date.now()) / 1000)),
  );
  useEffect(() => {
    const id = setInterval(() => {
      setRimasti(Math.max(0, Math.round((new Date(expiresAt).getTime() - Date.now()) / 1000)));
    }, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  return <span className="tabular-nums">{rimasti}s</span>;
}

export function SupportoPage() {
  const {
    enabled,
    phase,
    supervisoreNome,
    supervisoreOnline,
    errore,
    expiresAt,
    richiestaInCorso,
    connesso,
    riconnettiOra,
    invite,
    cancel,
  } = useSupport();

  if (!enabled) {
    return (
      <div className="mx-auto max-w-xl p-6">
        <h1 className="text-xl font-semibold">Supporto live</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Il supporto live non è attivo: chiedi all'amministratore di abilitarlo.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header ed errore restano nella colonna stretta: solo la vista chiamata
          (fase "connected") ha bisogno di tutta la larghezza per il video. */}
      <div className="mx-auto max-w-3xl">
        <header className="flex items-center gap-3">
          <LifeBuoy className="h-6 w-6 text-brand" aria-hidden="true" />
          <h1 className="text-xl font-semibold">Supporto live</h1>
        </header>

        {errore && (
          <p
            role="alert"
            className="mt-4 flex items-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800"
          >
            <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            {errore}
          </p>
        )}
        {!connesso && (
          <div
            role="status"
            className="mt-4 flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900"
          >
            <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span>
              Collegamento al supporto assente: non puoi chiamare né ricevere chiamate. Riprovo
              automaticamente…
            </span>
            <button
              type="button"
              onClick={riconnettiOra}
              className="rounded-md bg-amber-100 px-3 py-1 text-xs font-medium ring-1 ring-amber-300 transition-colors hover:bg-amber-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
            >
              Riprova ora
            </button>
          </div>
        )}
      </div>

      {phase === "connected" && (
        <div className="mt-5">
          <CallView />
        </div>
      )}

      {phase === "calling" && (
        <div className="mx-auto mt-6 max-w-3xl rounded-xl bg-surface p-6 text-center">
          <p className="text-sm">
            Sto chiamando <strong>{supervisoreNome}</strong>…
          </p>
          {expiresAt && (
            <p className="mt-1 text-xs text-muted-foreground">
              Attendo risposta per <Countdown expiresAt={expiresAt} />
            </p>
          )}
          <button
            type="button"
            onClick={cancel}
            className="mt-4 rounded-lg bg-surface px-4 py-2 text-sm ring-1 ring-black/10 transition-colors hover:bg-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
          >
            Annulla
          </button>
        </div>
      )}

      {(phase === "idle" || phase === "ended") && (
        <div className="mx-auto mt-6 max-w-3xl rounded-xl bg-surface p-6">
          <p id="supporto-info" className="text-sm">
            {supervisoreNome ? (
              <>
                Il tuo supervisore è <strong>{supervisoreNome}</strong>
                {supervisoreOnline ? " (online adesso)" : ""}. Riceve un avviso su Telegram anche se
                non è collegato al portale.
              </>
            ) : (
              "Nessun supervisore assegnato: chiedi all'amministratore di collegartene uno."
            )}
          </p>
          <button
            type="button"
            // `invite` e' asincrona e la fase passa a "calling" solo all'arrivo di
            // `ringing`: senza questo disabled il bottone resta cliccabile per 1-3s.
            // Senza WebSocket l'invito non parte: il `send` fallirebbe e la webcam
            // verrebbe accesa e spenta senza spiegazioni. Meglio bottone spento
            // piu' la banda che dice perche'.
            disabled={!supervisoreNome || richiestaInCorso || !connesso}
            aria-describedby="supporto-info"
            onClick={() => void invite(null)}
            className="mt-4 flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-muted-foreground disabled:opacity-50 disabled:hover:bg-muted-foreground"
          >
            <PhoneCall className="h-4 w-4" aria-hidden="true" />
            Chiedi aiuto al supervisore
          </button>
          <p className="mt-3 text-xs text-muted-foreground">
            Si apre una sessione con webcam, audio e condivisione schermo. Non viene registrata.
          </p>
        </div>
      )}
    </div>
  );
}
